import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import type { Subscription } from 'rxjs';
import { TenantAnalyzerService } from 'src/app/services/tenant-analyzer/tenant-analyzer.service';
import { NgClass } from '@angular/common';
import { ExternalLinkDirective } from '../../../directives/external-link/external-link.directive';


export interface IAnalyzerBannerConfig {
  icon: string;
  content: string;
  allowDismiss: boolean;
  action?: { label: string; url: string; id: string; };
  type: 'info' | 'warning' | 'danger' | 'success' | 'neutral';
  class?: string;
}

@Component({
    selector: 'bt-analyzer-banner',
    templateUrl: './analyzer-banner.component.html',
    styleUrls: ['./analyzer-banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, ExternalLinkDirective]
})

export class AnalyzerBannerComponent implements OnInit, OnDestroy {
  private tenantAnalyzerService = inject(TenantAnalyzerService);

  @Input() public config: IAnalyzerBannerConfig | undefined;
  public active = true;
  public tenantAnalyzerProgress = 0;
  public tenantAnalyzerSubscriber: Subscription | undefined;

  public content: string | undefined = undefined;

  ngOnInit(): void {
    this.content = this.config?.content;
    this.tenantAnalyzerSubscriber = this.tenantAnalyzerService.tenantAnalyzer$.subscribe(analyzerInfo => {
      if (!analyzerInfo?.steps || !this.config) {
        return;
      }

      const { current, total } = analyzerInfo.steps;
      const isInQueue = !analyzerInfo.isRunning?.isTouched;

      const stepsCompletedText = total > 0
        ? `${current} of ${total} steps completed`
        : '';

      this.content = `${this.config.content}: ${isInQueue ? 'in queue' : stepsCompletedText}`;

      // Ensure progress is always at least 1 if analysis has started but no progress is reported
      const defaultProgress = 1;
      this.tenantAnalyzerProgress = !isInQueue && current === 0 ? defaultProgress : analyzerInfo.progress || defaultProgress;
    }
    )
  }

  ngOnDestroy(): void {
    this.tenantAnalyzerSubscriber?.unsubscribe()
  }

  public executeAction(actionId: string) {
    switch (actionId) {
      case "cancelTenantAnalyzer":
        this.tenantAnalyzerSubscriber?.unsubscribe()
        this.tenantAnalyzerService.cancelTenantAnalyzer();
        break
    }
  }
}
